import { HtsReadinessPillarKey } from "../constants";
import { HtsReadinessPillarConfig } from "../schemas";

export const jobFit: HtsReadinessPillarConfig = {
  key: HtsReadinessPillarKey.JOB_FIT,
  label: "Job Fit",
  definition:
    "Scores the realism of the candidate's picture of the role and the honesty of their reckoning with its demands — not their competencies.",
  dataSourceNote:
    "Asked — two short questions: day-to-day realities including the hardest parts; how shift work/holidays/difficult situations fit their life. Follow-ups fire on thin, glamorized, or dismissive answers.",
  dimensions: [
    {
      key: "roleUnderstanding",
      label: "Role Understanding",
      weight: 0.4,
      anchors: {
        0: "A fantasy view of the role with no grasp of what the work actually involves.",
        25: "A surface understanding drawn from media portrayals — action-heavy, paperwork-free — or only routine steps (start the shift, drive around, wait for calls), with nothing harder than boredom or sameness named.",
        50: "A general grasp of the duties, thin on the hard parts.",
        75: "An accurate picture of the day-to-day realities, including the unglamorous parts, in the candidate's own words.",
        100: "A nuanced, realistic understanding — the hard parts named unprompted — informed by firsthand exposure such as a ride-along, shadowing, or real conversations with people doing the job, depending on the role being applied for, with specific, accurate detail that goes beyond public-facing descriptions of the role. Firsthand exposure is one way to reach this level; it is not required for it.",
      },
    },
    {
      key: "understandingOfDemands",
      label: "Understanding of the Demands",
      weight: 0.3,
      anchors: {
        0: "Has not considered the demands, or dismisses them (\"that stuff won't bother me\") and holds that position through the probe.",
        25: "Acknowledges the demands exist without engaging with them.",
        50: "A general acknowledgement — knows shifts and hard calls are part of it, but hasn't thought past the label.",
        75: "A specific understanding of what the demands actually do — to sleep, family routines, holidays, social life — stated concretely.",
        100: "A clear-eyed account of the cumulative weight of difficult exposure over a career, and awareness of the supports that make it sustainable.",
      },
    },
    {
      key: "lifeFitPreparedness",
      label: "Life Fit & Preparedness",
      weight: 0.3,
      anchors: {
        0: "No thought given to how the role fits their actual life, or an implausible claim that nothing would change.",
        25: "A vague \"I'll manage\", \"I'll adapt\" or \"I'll plan ahead\" with nothing behind it, or a plan for the job to fit around their current life (picking shifts so they don't miss anything).",
        50: "Some genuine reflection on their own circumstances, still general.",
        75: "Concrete thought about their specific commitments — named trade-offs, and how they would handle them.",
        100: "A realistic personal plan: arrangements considered, trade-offs named and accepted, support system engaged, honesty about which part will be hardest.",
      },
    },
  ],
  scoringNotes: [
    "A trade-off that shows the candidate expects the job to fit around them (for example, not missing late-night gaming) scores no higher than 50.",
  ],
  topIndicators: [
    "Paperwork, court, and repetition mentioned unprompted",
    "A real conversation with someone doing the job",
    "An honest sentence about which part will be hardest for them",
  ],
};
